<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'

  import { Routes } from '@/const/Routes'
  import { useAuthStore } from '@/stores/auth'
  import { useSubject } from '@/composables/useSubject'
  import { useTopic } from '@/composables/useTopic'

  import AppLayout from '@/components/layout/AppLayout.vue'
  import NavigateBackwardsLink from '@/components/layout/NavigateBackwardsLink.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import StandardLayout from '@/components/layout/Layout.vue'
  import SubjectContentList from '@/components/SubjectContentList.vue'
  import SubjectResources from '@/components/SubjectResources.vue'
  import TopicPageSkeleton from '@/components/skeletons/TopicPageSkeleton.vue'
  import Typography from '@/components/common/Typography.vue'
  import { useAppRouting } from '@/composables/useAppRouting'
  import VgsButton from '@/components/common/VgsButton.vue'

  const { getRouteWithParams } = useAppRouting()
  const { isTeacher } = storeToRefs(useAuthStore())
  const { t } = useI18n()
  const { path } = useRoute()

  const { currentTopic, isLoading, topicGuide, subjectContents, subjectResources } = useTopic()
  const { subject } = useSubject()
  const pupilSubjectResources = computed(() => subjectResources.value.filter((subjectResource) => !subjectResource.isTeacherOnly))
  const hasTeacherResources = computed(() => (subjectResources.value.length > pupilSubjectResources.value.length))

  const navigateBackwardsButtonText = computed(() => {
    let text = subject.value?.title
    const label = subject.value?.label

    if (text && label) {
      text = `${text} ${label}`
    }

    return text || t('app.back')
  })

</script>

<template>
  <StandardLayout>
    <NavigateBackwardsLink
      v-if="currentTopic?.title && subject"
      :button-text="navigateBackwardsButtonText"
    />
    <div class="topic">
      <AppLayout>
        <TopicPageSkeleton v-if="isLoading" />
        <template v-else-if="currentTopic">
          <div class="text">
            <Typography variant="h1">
              {{ currentTopic.title }}
            </Typography>

            <div class="introduction">
              <RichTextRenderer
                v-if="currentTopic.introduction"
                :rich-text="currentTopic.introduction"
              />
            </div>

            <router-link
              v-if="isTeacher && topicGuide"
              :to="getRouteWithParams(Routes.TopicGuide, topicGuide)"
              class="link"
            >
              <VgsButton
                type="label"
                variant="quaternary"
                shape="rounded"
                size="large"
                icon-left="info-circle"
              >
                {{ t('subject.to-teacher') }}
              </VgsButton>
            </router-link>
          </div>
        </template>
      </AppLayout>

      <AppLayout v-if="subjectContents.length">
        <div class="topic-middle">
          <SubjectContentList :subject-contents="subjectContents" />
        </div>
      </AppLayout>

      <AppLayout v-if="pupilSubjectResources?.length">
        <SubjectResources
          :title="t('topicPage.taskText')"
          :subject-resources="pupilSubjectResources"
          :link-route="Routes.SubjectResource"
        />
      </AppLayout>
      <AppLayout v-if="isTeacher && hasTeacherResources">
        <div class="resource-bank-wrapper">
          <Typography
            component="h2"
            variant="h3"
          >
            {{ t('topicPage.teacherContent.title') }}
          </Typography>
          <Typography component="p">
            {{ t('topicPage.teacherContent.text') }}
          </Typography>

          <router-link
            :to="`${path}/ressursbank`"
            class="resource-link"
          >
            <VgsButton
              v-if="isTeacher"
              type="label"
              variant="quaternary"
              shape="rounded"
            >
              {{ t('topicPage.teacherContent.exploreContent') }}
            </VgsButton>
          </router-link>
        </div>
      </AppLayout>
    </div>
  </StandardLayout>
</template>

<style lang="scss" scoped>
  .topic {
    display: flex;
    flex-direction: column;
    gap: 96px;
    justify-content: flex-start;

    .text {
      max-width: 895px;
    }

    .introduction {
      margin-bottom: 24px;

      :deep(.cms-p) {
        @include preamble;
      }
    }

    > * {
      width: 100%;
    }

    h1 {
      margin-bottom: 8px;
    }

    h2 {
      padding: 10px 0;
    }
  }

  .link {
    // Safari specific color + text-decoration
    color: inherit;
    text-decoration: none;

    &:focus-visible {
      box-shadow: none;

      .ks-button {
        @include focus-ring;
      }
    }
  }

  .resource-bank-wrapper {
    max-width: $vgs-max-width-content;
    margin: 0 auto;
    background-color: var(--color-orange-20);
    padding: 64px 24px;
    text-align: center;

    h2 {
      margin-bottom: 0
    }

    p {
      margin-top: 0;
    }
  }
</style>
